import * as cookie from 'cookie-es'
import { appendResponseHeader } from 'h3'

/**
 * Middleware that runs only on the server, and refreshes the user's access token if it has expired.
 *
 * It will update the relevant cookies, as well as pass them to subsequent requests using the event context.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const { session, loggedIn } = useUserSession()

  if (
    import.meta.client
      || to.name === 'login'
      || !loggedIn.value
      || (session.value?.expiresAt ?? 0) >= Date.now()
  ) {
    return
  }

  const { cookieNames } = useRuntimeConfig().public.session

  const event = useRequestEvent()

  try {
    await $fetch<void>('/api/auth/refresh', {
      // Pass event headers during SSR, so that cookies are present
      headers: event?.headers,
      retry: false,
      onResponse({ response }) {
        if (import.meta.client) {
          // On the client, cookie changes are handled automatically.
          return
        }

        // Forward refresh Set-Cookie headers so cookie attributes are preserved, so that new values are also available to later requests.
        const cookies: string[] = []
        for (const setCookie of response.headers.getSetCookie()) {
          const parsed = cookie.parseSetCookie(setCookie)
          if (!parsed) {
            continue
          }
          const { name, value } = parsed
          if ((name === cookieNames.token || name === cookieNames.user)) {
            if (event) {
              appendResponseHeader(event, 'set-cookie', setCookie)
            }

            if (value) {
              cookies.push(cookie.serialize(name, value))
            }
          }
        }

        if (event?.context) {
          event.context.updatedCookies = cookies
        }
      },
    })
  }
  catch (e) {
    console.error(e)
    // This probably means the user is not authenticated anymore, but that should be handled elsewhere, either by middleware or redirect after a failed fetch.
  }
})
